import { api } from './core';

// Office-Endpunkte für Lehrer-Rechnungen. Der Präfix `/api` ist Pflicht: nginx
// leitet nur `/api/*` an FastAPI `/api/v1/office/*` weiter, alles andere landet
// beim SPA-Fallback (index.html) und liefert still eine leere Liste.
const BASE = '/api/teacher-invoices';

export type TeacherInvoiceStatus = 'entwurf' | 'versendet' | 'bezahlt';

export interface TeacherInvoice {
  id: number;
  lehrerId: number;
  wpUserId?: number;
  lehrerName: string;
  lehrerEmail: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  periodFrom: string;
  periodTo: string;
  status: TeacherInvoiceStatus;
  shippingMethod: string;
  totalCents: number;
  createdAt: string;
}

export interface TeacherInvoiceDetail extends TeacherInvoice {
  message: string;
  positions: {
    id: number;
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    amountCents: number;
  }[];
}

export interface ListTeacherInvoicesParams {
  status?: string;
  search?: string;
  lehrerId?: number;
  wpUserId?: number;
  limit?: number;
}

export interface TeacherInvoiceListResponse {
  items?: TeacherInvoice[];
  total?: number;
}

export const teacherInvoicesApi = {
  list: async (params: ListTeacherInvoicesParams = {}): Promise<TeacherInvoiceListResponse> => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue;
      query.set(key, String(value));
    }
    const qs = query.toString();
    return api.get<TeacherInvoiceListResponse>(`${BASE}${qs ? `?${qs}` : ''}`);
  },
  get: async (id: number): Promise<TeacherInvoiceDetail> =>
    api.get<TeacherInvoiceDetail>(`${BASE}/${id}`),
  updateStatus: async (id: number, status: TeacherInvoiceStatus): Promise<{ ok: boolean }> =>
    api.put<{ ok: boolean }>(`${BASE}/${id}/status`, { status }),
  downloadUrl: async (id: number): Promise<{ url: string | null }> =>
    api.get<{ url: string | null }>(`${BASE}/${id}/download-url`),
};

export function dataUrlToBlob(url: string): Blob {
  const comma = url.indexOf(',');
  const header = url.slice('data:'.length, comma < 0 ? url.length : comma);
  const payload = comma < 0 ? '' : url.slice(comma + 1);
  const [mimeType, ...params] = header.split(';');
  const bytes = params.includes('base64')
    ? Uint8Array.from(atob(payload), (char) => char.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(payload));
  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

/**
 * Öffnet das Rechnungs-PDF in einem neuen Tab. Das Backend liefert eine
 * `data:`-URL; Browser blockieren das direkte Öffnen solcher URLs als Seite,
 * deshalb wird daraus vorher eine Blob-URL.
 */
export function openTeacherInvoiceDocument(url: string): void {
  if (!url.startsWith('data:')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const objectUrl = URL.createObjectURL(dataUrlToBlob(url));
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
